import type { PaletteMode, Shadows } from '@mui/material';
import { componentTokens } from './components';
import { ColorTokens, colorTokens, scaleTokens } from './semantic';

export {
  buildColorTokens,
  buildScaleTokens,
  colorTokens,
  scaleTokens,
} from './semantic';
export type { ColorTokens, ScaleTokens } from './semantic';
export { componentTokens, buildComponentTokens } from './components';
export type { ComponentTokens } from './components';

export type SvgPaints = {
  // Elementary paints in SVG assets, by value, and the color each takes in
  // the look.
  paints: Record<string, string>;
  white?: string;
  black?: string;
  // Dark ink of brand wordmarks, reversed on dark surfaces.
  brandInk?: string;
};

const elementarySvgPaints: SvgPaints = { paints: {} };

export const themeTokens = {
  ...scaleTokens,
  components: componentTokens,
  svgPaints: elementarySvgPaints,
};

export type LookTokens = {
  mode: PaletteMode;
  colors: ColorTokens;
  tokens: typeof themeTokens;
  // MUI elevations; without them the theme keeps MUI's default shadows.
  shadows?: Shadows;
};

export const elementaryLook: LookTokens = {
  mode: 'light',
  colors: colorTokens,
  tokens: themeTokens,
};

type Overrides<T> = {
  [K in keyof T]?: T[K] extends readonly unknown[]
    ? T[K]
    : T[K] extends object
    ? Overrides<T[K]>
    : T[K];
};

export type TokenOverrides<T> = Overrides<T>;

const isTokenGroup = (value: unknown): value is object =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const mergeGroup = (base: object, overrides: object): object =>
  Object.fromEntries(
    Object.entries(base).map(([key, value]) => {
      const override: unknown = Reflect.get(overrides, key);
      if (override === undefined) return [key, value];
      return [
        key,
        isTokenGroup(value) && isTokenGroup(override)
          ? mergeGroup(value, override)
          : override,
      ];
    }),
  );

// Overrides only replace existing tokens, so every look has the same token
// names and components never check which look is active.
export const mergeTokens = <T extends object>(
  base: T,
  overrides: Overrides<T>,
): T => mergeGroup(base, overrides) as T;
